import React, { useMemo, useRef, useEffect } from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import logo from "../assets/logo.webp";
import "./Navbar.css";

export default function Navbar() {
  const [searchHistory, setSearchHistory] = React.useState([]);
  const navigate = useNavigate();

  // GOOGLE TRANSLATE INIT
  useEffect(() => {
    if (window.googleTranslateLoaded) return; // ⭐ prevent re-loading

    window.googleTranslateLoaded = true;

    const script = document.createElement("script");
    script.src =
      "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
    document.body.appendChild(script);

    window.googleTranslateElementInit = () => {
      if (!window.google || !window.google.translate) return;

      new window.google.translate.TranslateElement(
        {
          pageLanguage: "en",
          includedLanguages: "en,hi,mr,gu,bn,ta,te,kn,ml,pa",
        },
        "google_translate_element"
      );
    };
  }, []);


  const changeLanguage = (langCode) => {
    const select = document.querySelector(".goog-te-combo");
    if (select) {
      select.value = langCode;
      select.dispatchEvent(new Event("change"));
    }
  };

  // AUTH
  const token = localStorage.getItem("eseva_token");
  const isAdmin = !!token;

  const adminData = localStorage.getItem("eseva_admin");
  let adminName = "";
  try {
    if (adminData) {
      const parsed = JSON.parse(adminData);
      adminName = parsed?.name || parsed?.username || "";
    }
  } catch {
    adminName = adminData;
  }

  const adminRole = (() => {
    try {
      return JSON.parse(localStorage.getItem("eseva_admin"))?.role || "";
    } catch {
      return "";
    }
  })();

  // COLLAPSE LOGIC
  const collapseRef = useRef(null);
  const togglerRef = useRef(null);

  useEffect(() => {
    if (!window.bootstrap || !collapseRef.current) return;

    const instance = new window.bootstrap.Collapse(collapseRef.current, {
      toggle: false,
    });

    collapseRef.current.bsCollapse = instance;
  }, []);

  const closeMenu = () => {
    const instance = collapseRef.current?.bsCollapse;
    if (instance && collapseRef.current.classList.contains("show")) {
      instance.hide();
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("eseva_token");
    localStorage.removeItem("eseva_admin");
    closeMenu();
    navigate("/");
  };

  // SEARCH MODAL
  const openSearchModal = () => {
    const modalEl = document.getElementById("searchModal");
    const modal = new window.bootstrap.Modal(modalEl);

    const input = document.getElementById("modalSearchInput");
    if (input) input.value = "";

    const saved = JSON.parse(localStorage.getItem("eseva_search_history") || "[]");
    setSearchHistory(saved);

    modal.show();

    modalEl.addEventListener(
      "shown.bs.modal",
      () => {
        const inputField = document.getElementById("modalSearchInput");
        if (inputField) inputField.focus();
      },
      { once: true }
    );
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();

    const q = document.getElementById("modalSearchInput").value.trim();
    if (!q) return;

    let history = JSON.parse(localStorage.getItem("eseva_search_history") || "[]");
    history = history.filter((i) => i !== q);
    history.unshift(q);
    history = history.slice(0, 8);

    localStorage.setItem("eseva_search_history", JSON.stringify(history));
    setSearchHistory(history);

    const modalEl = document.getElementById("searchModal");
    const modalInstance = window.bootstrap.Modal.getInstance(modalEl);
    if (modalInstance) modalInstance.hide();

    setTimeout(() => {
      navigate(`/search?q=${encodeURIComponent(q)}`);
    }, 200);
  };

  const clearHistory = () => {
    localStorage.removeItem("eseva_search_history");
    setSearchHistory([]);
  };

  return (
    <>
      <div id="google_translate_element" style={{ display: "none" }}></div>

      <nav className="navbar navbar-expand-lg navbar-dark custom-navbar shadow-sm sticky-top">
        <div className="container d-flex justify-content-between">

          {/* LEFT — LOGO */}
          <Link className="navbar-brand d-flex align-items-center" to="/" onClick={closeMenu}>
            <img
              src={logo}
              alt="eSevaLink"
              width="168"
              height="42"
              style={{ height: 42, width: 168, marginRight: 8 }}
              loading="eager"
              fetchpriority="high"
            />
          </Link>

          {/* MOBILE SEARCH + TOGGLER */}
          <div className="d-flex d-lg-none align-items-center ms-auto">
            <button className="btn p-0 me-2" onClick={openSearchModal} style={{ color: "white" }}>
              <i className="bi bi-search" style={{ fontSize: 22 }}></i>
            </button>

            <button
              className="navbar-toggler p-0"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#mainNav"
              ref={togglerRef}
            >
              <span className="navbar-toggler-icon"></span>
            </button>
          </div>

          {/* RIGHT SIDE NAV */}
          <div className="collapse navbar-collapse justify-content-end" id="mainNav" ref={collapseRef}>

            {/* DESKTOP SEARCH BAR */}
            {/* DESKTOP SEARCH BAR — ICON INSIDE INPUT */}
            <div className="d-none d-lg-flex align-items-center flex-grow-1 mx-3">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const q = document.getElementById("desktopSearchInput").value.trim();
                  if (!q) return;

                  let history = JSON.parse(localStorage.getItem("eseva_search_history") || "[]");
                  history = [q, ...history.filter((i) => i !== q)].slice(0, 8);
                  localStorage.setItem("eseva_search_history", JSON.stringify(history));

                  navigate(`/search?q=${encodeURIComponent(q)}`);
                }}
                className="w-100 desktop-search-wrapper"
              >
                <i className="bi bi-search search-input-icon"></i>

                <input
                  id="desktopSearchInput"
                  type="text"
                  className="form-control search-input-inside"
                  placeholder="Aadhar Card Update, Apply Pan Card... "
                />
              </form>
            </div>


            {/* PUBLIC NAV */}
            {!isAdmin && (
              <ul className="navbar-nav d-flex align-items-center gap-2">

                <li className="nav-item">
                  <NavLink className={({ isActive }) =>
                    isActive ? "nav-link active" : "nav-link"
                  } to="/" onClick={closeMenu} end>
                    Home
                  </NavLink>
                </li>

                <li className="nav-item">
                  <NavLink className={({ isActive }) =>
                    isActive ? "nav-link active" : "nav-link"
                  } to="/about" onClick={closeMenu}>
                    About Us
                  </NavLink>
                </li>

                <li className="nav-item">
                  <NavLink className={({ isActive }) =>
                    isActive ? "nav-link active" : "nav-link"
                  } to="/contact" onClick={closeMenu}>
                    Contact
                  </NavLink>
                </li>

                {/* LANGUAGE DROPDOWN */}
                <li className="nav-item dropdown">
                  <button className="btn nav-link dropdown-toggle text-white" data-bs-toggle="dropdown">
                    🌐 Language
                  </button>

                  <ul className="dropdown-menu dropdown-menu-end">
                    <li><button className="dropdown-item" onClick={() => changeLanguage("en")}>English</button></li>
                    <li><button className="dropdown-item" onClick={() => changeLanguage("hi")}>हिन्दी</button></li>
                    <li><button className="dropdown-item" onClick={() => changeLanguage("mr")}>मराठी</button></li>
                    <li><button className="dropdown-item" onClick={() => changeLanguage("gu")}>ગુજરાતી</button></li>
                    <li><button className="dropdown-item" onClick={() => changeLanguage("ta")}>தமிழ்</button></li>
                    <li><button className="dropdown-item" onClick={() => changeLanguage("te")}>తెలుగు</button></li>
                    <li><button className="dropdown-item" onClick={() => changeLanguage("kn")}>ಕನ್ನಡ</button></li>
                  </ul>
                </li>

              </ul>
            )}

            {/* ADMIN NAV */}
            {/* ADMIN NAV */}
            {isAdmin && (
              <ul className="navbar-nav ms-auto">

                <li className="nav-item dropdown">
                  <button
                    className="btn nav-link dropdown-toggle text-white mx-2"
                    data-bs-toggle="dropdown"
                  >
                    ⚙️ Admin Panel
                  </button>

                  <ul className="dropdown-menu dropdown-menu-end">




                    <li>
                      <NavLink className="dropdown-item" to="/admin/manage-slider" onClick={closeMenu}>
                        Manage Slider
                      </NavLink>
                    </li>

                    <Link to="/admin/latest-manager" className="dropdown-item">
                      Manage Latest Updates
                    </Link>


                    <li>
                      <NavLink className="dropdown-item" to="/admin/schemes" onClick={closeMenu}>
                        Manage Schemes
                      </NavLink>
                    </li>

                    <li>
                      <NavLink className="dropdown-item" to="/admin/mostly-used" onClick={closeMenu}>
                        Manage Mostly Used Links
                      </NavLink>
                    </li>

                    <li>
                      <NavLink className="dropdown-item" to="/admin/feedbacks" onClick={closeMenu}>
                        Manage Feedbacks
                      </NavLink>
                    </li>

                    <li>
                      <NavLink className="dropdown-item" to="/admin" onClick={closeMenu}>
                        Add New Scheme
                      </NavLink>
                    </li>
                    <li>
                      <NavLink className="dropdown-item" to="/admin/manage-about" onClick={closeMenu}>
                        Manage About Us
                      </NavLink>
                    </li>

                    {adminRole === "superadmin" && (
                      <li>
                        <NavLink className="dropdown-item" to="/admin/signup" onClick={closeMenu}>
                          Add New Admin
                        </NavLink>
                      </li>
                    )}

                  </ul>
                </li>

                <li className="nav-item d-flex align-items-center text-light fw-semibold ms-2">
                  👋 Hi, {adminName || "Admin"}
                </li>

                <li className="nav-item">
                  <button
                    type="button"
                    className="btn btn-outline-light ms-3"
                    onClick={handleLogout}
                  >
                    Logout
                  </button>
                </li>

              </ul>
            )}




          </div>
        </div>
      </nav>

      {/* SEARCH MODAL (Mobile) */}
      <div className="modal fade" id="searchModal" tabIndex="-1" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content p-3" style={{ borderRadius: "12px" }}>
            <div className="modal-header border-0">
              <h5 className="modal-title fw-semibold">Search Services</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal"></button>
            </div>

            <form onSubmit={handleSearchSubmit}>
              <div className="modal-body">
                <input
                  id="modalSearchInput"
                  className="form-control form-control-lg"
                  placeholder="Example: Aadhar Update, PAN Card..."
                />

                {searchHistory.length > 0 && (
                  <div className="mt-3">
                    <div className="d-flex justify-content-between">
                      <h6 className="fw-semibold text-muted mb-0">Recent Searches</h6>
                      <button type="button" onClick={clearHistory} className="btn btn-sm text-danger fw-bold">
                        Clear
                      </button>
                    </div>

                    <ul className="list-group shadow-sm mt-2">
                      {searchHistory.map((item, index) => (
                        <li
                          key={index}
                          className="list-group-item list-group-item-action"
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            navigate(`/search?q=${encodeURIComponent(item)}`);
                            const modalEl = document.getElementById("searchModal");
                            const modalInstance = window.bootstrap.Modal.getInstance(modalEl);
                            if (modalInstance) modalInstance.hide();
                          }}
                        >
                          <i className="bi bi-clock-history me-2 text-primary"></i>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <div className="modal-footer border-0">
                <button className="btn w-100" style={{ backgroundColor: "#fe6e03", color: "white" }}>
                  Search
                </button>
              </div>
            </form>

          </div>
        </div>
      </div>
    </>
  );
}
